import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { speculationRules } from '@theme/speculation-rules';
import { startViewTransition } from '@theme/utilities';

class CustomVariantPickerComponent extends Component {
  #abortController = null;

  #visibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupVisibilityPrefetch();
    this.addEventListener('pointerenter', this.#prefetchLink, true);
    this.addEventListener('focusin', this.#prefetchLink);
    this.addEventListener('click', this.#navigateOnFirstClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#teardownVisibilityPrefetch();
    this.removeEventListener('pointerenter', this.#prefetchLink, true);
    this.removeEventListener('focusin', this.#prefetchLink);
    this.removeEventListener('click', this.#navigateOnFirstClick);
  }

  #setupVisibilityPrefetch() {
    if (typeof IntersectionObserver !== 'function') {
      this.#prefetchAllLinks();
      return;
    }

    this.#visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.#prefetchAllLinks();
      this.#teardownVisibilityPrefetch();
    });

    this.#visibilityObserver.observe(this);
  }

  #teardownVisibilityPrefetch() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  #prefetchAllLinks() {
    const links = Array.from(this.querySelectorAll('a[href]')).filter((link) => !this.#isCurrentLink(link));
    if (!links.length) return;

    speculationRules.prefetch(links.map((link) => link.href));
  }

  #prefetchLink = (event) => {
    const link = this.#getLinkFromEvent(event);
    if (!link || this.#isCurrentLink(link)) return;

    speculationRules.prefetch(link.href);
  };

  #navigateOnFirstClick = (event) => {
    const link = this.#getLinkFromEvent(event);
    if (!link) return;

    if (this.#isCurrentLink(link)) {
      event.preventDefault();
      return;
    }

    if (!(event instanceof MouseEvent)) return;
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    this.#navigateToProduct(link.href);
  };

  async #navigateToProduct(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
      });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const main = document.querySelector('main');
      const newMain = html.querySelector('main');

      if (!(main instanceof HTMLElement) || !(newMain instanceof HTMLElement)) {
        window.location.href = url;
        return;
      }

      await startViewTransition(() => {
        morph(main, newMain);
        this.#syncDocumentState(html, url);
      }, ['product-image-transition']);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      this.#abortController = null;
    }
  }

  #syncDocumentState(html, url) {
    const title = html.querySelector('title')?.textContent?.trim();
    if (title) {
      document.title = title;
    }

    const canonical = html.querySelector('link[rel="canonical"]')?.getAttribute('href');
    const currentCanonical = document.querySelector('link[rel="canonical"]');
    if (canonical && currentCanonical instanceof HTMLLinkElement) {
      currentCanonical.href = canonical;
    }

    const description = html.querySelector('meta[name="description"]')?.getAttribute('content');
    const currentDescription = document.querySelector('meta[name="description"]');
    if (description && currentDescription instanceof HTMLMetaElement) {
      currentDescription.content = description;
    }

    const productId = html.querySelector('variant-picker')?.dataset.productId;
    if (productId) {
      RecentlyViewed.addProduct(productId);
    }

    history.replaceState({}, '', url);
  }

  #getLinkFromEvent(event) {
    if (!(event.target instanceof Element)) return null;

    const link = event.target.closest('a[href]');
    return link instanceof HTMLAnchorElement ? link : null;
  }

  #isCurrentLink(link) {
    const href = link.getAttribute('href') ?? '';
    if (!href || href === '#') return true;
    if (link.getAttribute('aria-current') === 'page' || link.classList.contains('active')) return true;

    try {
      const linkUrl = new URL(link.href, window.location.origin);
      return `${linkUrl.pathname}${linkUrl.search}` === `${window.location.pathname}${window.location.search}`;
    } catch {
      return true;
    }
  }
}

if (!customElements.get('custom-variant-picker-component')) {
  customElements.define('custom-variant-picker-component', CustomVariantPickerComponent);
}
